import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  LeftOutlined, RightOutlined, CloseOutlined,
  ExpandOutlined, PictureOutlined, ZoomInOutlined, ZoomOutOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ListingImage } from '../hooks/useListingDetails';

interface ImageGalleryProps {
  images: ListingImage[];
  title: string;
}

interface Pan { x: number; y: number }

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, title }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex]   = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Zoom / pan state
  const [zoom, setZoom]       = useState(1);
  const [pan, setPan]         = useState<Pan>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart             = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);

  const hasImages = images.length > 0;

  /* ── reset zoom when changing image ── */
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  /* ── thumbnail nav ── */
  const prevThumb = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const nextThumb = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  /* ── lightbox nav ── */
  const openLightbox = () => { setLightboxIndex(activeIndex); resetZoom(); setLightboxOpen(true); };
  const closeLightbox = () => { setLightboxOpen(false); resetZoom(); };

  const lightboxPrev = useCallback(() => {
    resetZoom();
    setLightboxIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const lightboxNext = useCallback(() => {
    resetZoom();
    setLightboxIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  /* ── zoom helpers ── */
  const zoomIn  = (e: React.MouseEvent) => { e.stopPropagation(); setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM)); };
  const zoomOut = (e: React.MouseEvent) => { e.stopPropagation(); setZoom((z) => { const nz = Math.max(z - ZOOM_STEP, MIN_ZOOM); if (nz === 1) setPan({ x: 0, y: 0 }); return nz; }); };

  /* ── click image to toggle 2x / reset ── */
  const handleImgClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    e.stopPropagation();
    setZoom((z) => {
      const nz = z === 1 ? 2 : 1;
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  };

  /* ── mouse wheel zoom ── */
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    setZoom((z) => {
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta));
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  }, []);

  /* ── drag to pan ── */
  const onMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    setIsDragging(false);
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    e.preventDefault();
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.mx;
    const dy = e.clientY - dragStart.current.my;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setIsDragging(true);
    setPan({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
  };

  const onMouseUp = () => { dragStart.current = null; setIsDragging(false); };

  /* ── keyboard ── */
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  { lightboxPrev(); }
      else if (e.key === 'ArrowRight') { lightboxNext(); }
      else if (e.key === 'Escape')     { closeLightbox(); }
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
      else if (e.key === '-') setZoom((z) => { const nz = Math.max(z - ZOOM_STEP, MIN_ZOOM); if (nz === 1) setPan({ x: 0, y: 0 }); return nz; });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxPrev, lightboxNext]);

  /* ── wheel listener on the img-wrap (non-passive) ── */
  const imgWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = imgWrapRef.current;
    if (!el || !lightboxOpen) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [lightboxOpen, handleWheel]);

  /* ── body scroll lock ── */
  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightboxOpen]);

  if (!hasImages) {
    return (
      <div className="gallery-empty">
        <PictureOutlined />
        <span>{t('listing_details.no_photos_available')}</span>
      </div>
    );
  }

  const currentImage  = images[activeIndex];
  const lightboxImage = images[lightboxIndex];

  return (
    <>
      {/* ── Thumbnail gallery ── */}
      <div className="gallery-root">
        <div className="gallery-main" onClick={openLightbox}>
          <img src={currentImage.url} alt={currentImage.alt || title} loading="lazy" className="gallery-main-img" />
          <div className="gallery-counter">
            <PictureOutlined style={{ fontSize: 12 }} />
            {activeIndex + 1} / {images.length}
          </div>
          <div className="gallery-fullscreen-hint">
            <ExpandOutlined /> {t('listing_details.view_all_photos')}
          </div>
          {images.length > 1 && (
            <>
              <button className="gallery-arrow gallery-arrow-left" onClick={prevThumb} aria-label="Previous"><LeftOutlined /></button>
              <button className="gallery-arrow gallery-arrow-right" onClick={nextThumb} aria-label="Next"><RightOutlined /></button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="gallery-thumbs">
            {images.map((img, idx) => (
              <button
                key={img.id}
                className={`gallery-thumb ${idx === activeIndex ? 'gallery-thumb-active' : ''}`}
                onClick={() => setActiveIndex(idx)}
                aria-label={`Photo ${idx + 1}`}
              >
                <img src={img.url} alt={img.alt || `${title} ${idx + 1}`} loading="lazy" />
                {idx === activeIndex && <div className="gallery-thumb-overlay" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Fullscreen lightbox ── */}
      {lightboxOpen && ReactDOM.createPortal(
        <div className="lightbox-overlay" onClick={closeLightbox}>

          {/* Top bar */}
          <div className="lightbox-topbar" onClick={(e) => e.stopPropagation()}>
            <span className="lightbox-counter">{lightboxIndex + 1} / {images.length}</span>

            <div className="lightbox-zoom-controls">
              <button className="lightbox-zoom-btn" onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">
                <ZoomOutOutlined />
              </button>
              <span className="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="lightbox-zoom-btn" onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">
                <ZoomInOutlined />
              </button>
            </div>

            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
              <CloseOutlined />
            </button>
          </div>

          {/* Image + arrows */}
          <div
            ref={imgWrapRef}
            className="lightbox-img-wrap"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
          >
            {images.length > 1 && (
              <button
                className="lightbox-arrow lightbox-arrow-left"
                onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
                aria-label="Previous"
                style={{ opacity: zoom > 1 ? 0.3 : 1 }}
              >
                <LeftOutlined />
              </button>
            )}

            <img
              src={lightboxImage.url}
              alt={lightboxImage.alt || title}
              className="lightbox-img"
              draggable={false}
              onClick={handleImgClick}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
              }}
            />

            {images.length > 1 && (
              <button
                className="lightbox-arrow lightbox-arrow-right"
                onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
                aria-label="Next"
                style={{ opacity: zoom > 1 ? 0.3 : 1 }}
              >
                <RightOutlined />
              </button>
            )}
          </div>

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="lightbox-thumbs" onClick={(e) => e.stopPropagation()}>
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  className={`lightbox-thumb ${idx === lightboxIndex ? 'lightbox-thumb-active' : ''}`}
                  onClick={() => { setLightboxIndex(idx); resetZoom(); }}
                  aria-label={`Photo ${idx + 1}`}
                >
                  <img src={img.url} alt={`${title} ${idx + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default ImageGallery;
